import { GroupBase } from "chakra-react-select";
import { ChakraStylesConfig } from "chakra-react-select/dist/types/types";
import clsx from "clsx";
import React, { FunctionComponent, useMemo } from "react";
import { SelectInstance } from "react-select";
import { SelectComponentsConfig } from "react-select/dist/declarations/src/components";
import { selectButtonComponentsConfig } from "shared/components/input/select/SelectButtonComponents";
import { selectButtonChakraStyles } from "shared/components/input/select/SelectButtonStyles";
import {
  SelectBaseProps,
  SelectInternal,
} from "shared/components/input/select/SelectInternal";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import {
  flattenSelectOptions,
  getSelectOptionsFromChildren,
  selectValueIsMulti,
} from "shared/components/input/select/SelectUtils";
import { FormInputValue } from "shared/types/FormInputValue";

export interface SelectButtonProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> extends SelectBaseProps<T, IsMulti> {
  title?: string;
  children?: React.ReactNode;
  minWidth?: string;
  chakraStyles?: ChakraStylesConfig<SelectOptionData, IsMulti>;
  components?: SelectComponentsConfig<
    SelectOptionData,
    IsMulti,
    GroupBase<SelectOptionData>
  >;
  size?: "sm" | "md";
  isFilterButton?: boolean;
}

// This type is needed so the generic type args are not lost when using forwardRef
export type SelectButton = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  props: SelectButtonProps<T, IsMulti> & {
    ref?: React.Ref<SelectInstance<SelectOptionData, IsMulti>>;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const SelectButton = React.forwardRef(
  <T extends FormInputValue = string, IsMulti extends boolean = false>(
    {
      children,
      title,
      chakraStyles = selectButtonChakraStyles,
      components = selectButtonComponentsConfig,
      isOptional,
      value: valueProp,
      ...props
    }: SelectButtonProps<T, IsMulti>,
    ref: React.Ref<SelectInstance<SelectOptionData, IsMulti>>,
  ) => {
    const optionsFromChildren = useMemo(
      () => getSelectOptionsFromChildren(children),
      [children],
    );
    const value =
      props.isFilterButton && selectValueIsMulti(valueProp, props.isMulti)
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          valueProp ??
          flattenSelectOptions(optionsFromChildren).map((o) => o.value)
        : valueProp;
    const valueIsMulti = selectValueIsMulti(value, props.isMulti);
    return (
      <SelectInternal<T, IsMulti>
        ref={ref}
        value={value}
        options={optionsFromChildren}
        placeholder={title}
        chakraStyles={chakraStyles}
        components={components}
        isOptional={isOptional}
        className={clsx({
          "react-select--is-filter-button": props.isFilterButton,
          "react-select--is-all-selected":
            valueIsMulti && value.length === optionsFromChildren.length,
          "react-select--is-none-selected": valueIsMulti && value.length === 0,
        })}
        {...props}
      />
    );
  },
) as SelectButton;

(SelectButton as FunctionComponent).displayName = "SelectButton";
